import React, { useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { PersonDetailsSummary } from "./components/PersonDetailsSummary";
import EventLabel from "../../components/EventLabel";
import { OrderTotalSummary } from "./components/OrderTotalSummary";
import { ConfirmationOrderRow, getCalculatedOrderRow } from "./components/ConfirmationOrderRow";
import { toCamelCase } from "../../helpers/objectHelper";

const TableHeader = ({ displayConfirmationSettings }) => {
  return (
    <tr>
      <th>
        <EventLabel name="Name" groupName="CMS.OrderSummary" />
      </th>
      {displayConfirmationSettings["ShowConfRowStatus"] ? (
        <th>
          <EventLabel name="OrderRowStatus" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
      {displayConfirmationSettings["ShowConfStartDate"] || displayConfirmationSettings["ShowConfStartTime"] ? (
        <th align="left">
          <EventLabel name="StartDateTime" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
      {displayConfirmationSettings["ShowConfEndDate"] || displayConfirmationSettings["ShowConfEndTime"] ? (
        <th align="left">
          <EventLabel name="EndDateTime" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
      {displayConfirmationSettings["ShowConfAmount"] ? (
        <th align="right">
          <EventLabel name="Amount" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
      {displayConfirmationSettings["ShowConfDiscount"] ? (
        <th align="right">
          <EventLabel name="DiscountAmount" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
      {displayConfirmationSettings["ShowConfVatAmount"] ? (
        <th align="right">
          <EventLabel name="VatAmount" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
      {displayConfirmationSettings["ShowConfVatPercent"] ? (
        <th align="right">
          <EventLabel name="VatPercent" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
      {displayConfirmationSettings["ShowConfQuantity"] ? (
        <th align="right">
          <EventLabel name="Quantity" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
      {displayConfirmationSettings["ShowConfRowTotal"] ? (
        <th align="right">
          <EventLabel name="RowTotal" groupName="CMS.OrderSummary" />
        </th>
      ) : null}
    </tr>
  );
};

export const OrderSummary = observer((props) => {
  const { displayConfirmationSettings, breakouts, orders, settings, mainEvent, loadHotels, hotelAllotments, hotels } = props;
  const { overrideDisplay = false, showOrderDetails = true, showOnlyHotelOrderRows = false, showPersonDetails = false } = settings;

  useEffect(() => {
    if (orders.some((o) => o.orderRow.some((or) => or.hotelGroupId)) && hotelAllotments?.length === 0) {
      loadHotels();
    }
  }, [orders, hotelAllotments]);

  const overridenDisplaySettings = useMemo(() => {
    const o = {};
    for (const key in displayConfirmationSettings) {
      if (Object.hasOwnProperty.call(displayConfirmationSettings, key)) {
        o[key] = settings[toCamelCase(key)] === true;
      }
    }
    return o;
  }, [
    settings?.showConfStartDate,
    settings?.showConfStartTime,
    settings?.showConfEndDate,
    settings?.showConfEndTime,
    settings?.showConfAmount,
    settings?.showConfDiscount,
    settings?.showConfVatAmount,
    settings?.showConfVatPercent,
    settings?.showConfQuantity,
    settings?.showConfRowTotal,
    settings?.showConfFreeRows,
    settings?.showConfOrderTotalSum,
    settings?.showConfMainEvent,
    settings?.showConfDescription,
    settings?.showConfRowStatus,
    settings?.showConfCancelledRows,
    settings?.showOnlyHotelOrderRows,
  ]);

  const showCancelledRows = (overrideDisplay && overridenDisplaySettings["ShowConfCancelledRows"]) || (!overrideDisplay && displayConfirmationSettings["ShowConfCancelledRows"]);
  const flatOrderRowsList = orders
    .filter((o) => o.orderRow)
    .map((o) => [
      ...o.orderRow
        .filter((or) => (showCancelledRows ? true : or.status != "Cancelled"))
        .map((or) => getCalculatedOrderRow(or, breakouts, orders, o.orderId, mainEvent))
        .filter((or) => Boolean(or)),
    ])
    .flat();

  const hotelAllotmentRows = useMemo(() => {
    const orderRows = orders.flatMap((o) => o.orderRow);
    if (hotels?.length === 0 || hotelAllotments?.length === 0) return [];

    const result = orderRows
      .filter((o) => o.hotelGroupId)
      .reduce((acc, orderRow) => {
        const hotelAllotment = hotelAllotments.find((ha) => ha.hotelAllotmentPricing?.some((hap) => hap.allotmentPricingId === orderRow?.hotelAllotmentPricingId));


        if (!hotelAllotment) return acc;

        const hotel = hotels.find((h) => h.hotelRoomType.some((y) => y.hotelRoomTypeId == hotelAllotment.hotelRoomTypeId));
        const roomType = hotel.hotelRoomType.find((hr) => hr.hotelRoomTypeId == hotelAllotment.hotelRoomTypeId);

        const pricingIds = orderRows.filter((or) => or.hotelGroupId === orderRow.hotelGroupId).map((or) => or.hotelAllotmentPricingId);

        const dateValues = hotelAllotments.filter((ha) => ha.hotelAllotmentPricing?.some((hap) => pricingIds.includes(hap.allotmentPricingId))).map((ha) => +new Date(ha.date));
        const allotmentStartDate = Math.min(...dateValues);
        const allotmentEndDate = Math.max(...dateValues);

        if (!hotelAllotment) return acc;

        const index = acc.findIndex((a) => a.hotelGroupId === orderRow.hotelGroupId);

        if (index !== -1) {
          acc[index].amount += orderRow.amount;
          acc[index].rowTotal += orderRow.amount;
          return acc;
        }
        return [
          ...acc,
          {
            ...orderRow,
            rowTotal: orderRow.amount,
            name: ` ${hotel.name} - ${roomType.name}`,
            startDateTime: new Date(allotmentStartDate).toLocaleDateString(),
            endDateTime: new Date(allotmentEndDate).toLocaleDateString(),
          },
        ];
      }, []);

    console.log(result);

    return result;
  }, [hotelAllotments, orders, hotels]);

  if (showOnlyHotelOrderRows) {

    console.log(hotelAllotmentRows)
    return (
      <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
        {orders.map((o, i) => (
          <div className="cms-table-container">
            <table className="cms-table small" style={{ overflowX: "auto" }} aria-label="spanning table">
              <thead>
                <TableHeader {...{ displayConfirmationSettings: overrideDisplay ? overridenDisplaySettings : displayConfirmationSettings }} />
              </thead>
              <tbody>
                {hotelAllotmentRows
                  ?.filter((or) => or.orderId === o.orderId)
                  .map((row) => (
                    <ConfirmationOrderRow
                      {...props}
                      displayConfirmationSettings={overrideDisplay ? overridenDisplaySettings : displayConfirmationSettings}
                      mainEvent={mainEvent}
                      key={row.hotelAllotmentPricingId}
                      orderRow={row}
                    />
                  ))}
              </tbody>
            </table>
          </div>
        ))}
        <OrderTotalSummary {...props} flatOrderRows={hotelAllotmentRows} />
      </div>
    );
  }

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
      {orders.map((o, i) => {
        return (
          <div key={i} style={{ width: "100%", display: "block" }}>
            {((Object.entries(o?.person ?? {})?.length > 0 && Object.entries(o?.person ?? {})?.some(([_, value]) => value)) ||
              Object.entries(o?.invoice ?? {})?.length > 0 ||
              (Object.entries(o?.company ?? {})?.length > 0 && Object.entries(o?.company ?? {})?.some(([_, value]) => value)) ||
              (o?.answer?.length > 0 && o?.answer?.some(({ answerRow }) => answerRow?.length > 0))) &&
            showPersonDetails ? (
              <div className="cms-order-summary-card">
                <PersonDetailsSummary order={o} {...props} />
              </div>
            ) : null}
            <>
              {o?.orderRow ? (
                <div className="cms-table-container">
                  <table className="cms-table small" style={{ overflowX: "auto" }} aria-label="spanning table">
                    <thead>
                      <TableHeader {...{ displayConfirmationSettings: overrideDisplay ? overridenDisplaySettings : displayConfirmationSettings }} />
                    </thead>
                    <tbody>
                      {flatOrderRowsList
                        ?.filter((x) => x.orderId === o.orderId)
                        ?.sort((a, b) => a.sortOrder - b.sortOrder)
                        ?.map((row) => (
                          <ConfirmationOrderRow {...props} displayConfirmationSettings={overrideDisplay ? overridenDisplaySettings : displayConfirmationSettings} mainEvent={mainEvent} key={row.eventId} orderRow={row} />
                        ))}

                      {hotelAllotmentRows
                        .filter((or) => or.orderId === o.orderId)
                        .map((row) => (
                          <ConfirmationOrderRow
                            {...props}
                            displayConfirmationSettings={overrideDisplay ? overridenDisplaySettings : displayConfirmationSettings}
                            mainEvent={mainEvent}
                            key={row.hotelAllotmentPricingId}
                            orderRow={row}
                          />
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </>
          </div>
        );
      })}
      {showOrderDetails &&
      flatOrderRowsList &&
      flatOrderRowsList.length > 0 &&
      ((overrideDisplay && overridenDisplaySettings["ShowConfOrderTotalSum"]) || (!overrideDisplay && displayConfirmationSettings["ShowConfOrderTotalSum"])) ? (
        <OrderTotalSummary {...props} flatOrderRows={flatOrderRowsList} />
      ) : null}
    </div>
  );
});
